import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store';
import {
  NotificationTemplateTypes,
  notificationTemplatesSettingsTree,
  getPrepareNotificationsSettings,
} from '@ui/src';
import {
  onPageChange,
  onSectionChange,
  handleConfirmPopup,
  onSearch,
} from './store/slices';
import {
  onChangeHelperArray,
  onChangeHelperSingle,
} from './helpers/on-change-helper';
import ColumnWithConfirmHOC from './column-with-comfirm';
import ColumnWithToggle from '../../components/notification-settings/column-with-toggle';
import { useEditTemplateMutation } from './store/services';
import sectionActions from './helpers/get-section-actions';
import { SettingsColumnTypes } from './types';

import cn from './notification-settings.module.css';

const SettingsColumn = ({ isLoading }: SettingsColumnTypes) => {
  const dispatch = useAppDispatch();

  const {
    selectedTemplate,
    templateId,
    sectionId,
    pageId,
    search,
    templateTypes,
    allActionsSectionFiltered,
  } = useAppSelector((state) => state.notificationSettings.templates);

  const [editTemplate, { isLoading: requestInProgress, isSuccess }] =
    useEditTemplateMutation();

  const [values, setValues] = useState(
    selectedTemplate?.notificationsTypes || [],
  );

  const typesEnumKeys = Object.keys(NotificationTemplateTypes).map(
    (item) => item,
  );
  const unassignedTypes = templateTypes?.filter((item) =>
    typesEnumKeys.every((type) => item !== type),
  );

  let nextNotificationTeplatesSettingsTree = notificationTemplatesSettingsTree;

  if (unassignedTypes?.length) {
    nextNotificationTeplatesSettingsTree = [
      ...nextNotificationTeplatesSettingsTree,
      {
        key: 'SECTION_UNASSIGNED' as NotificationTemplateTypes,
        pages: [
          {
            key: 'UNASSIGNED_LIST' as NotificationTemplateTypes,
            actions: unassignedTypes
              .filter((item) => item.includes('_ACTION'))
              .map((item) => ({
                key: item,
              })),
          },
        ],
      },
    ];
  }

  const columnsData = getPrepareNotificationsSettings(
    nextNotificationTeplatesSettingsTree,
  );

  const sections = templateId ? columnsData : undefined;
  const pages =
    templateId && sectionId ? columnsData[sectionId]?.pages : undefined;
  const actions =
    templateId && sectionId && pageId && pageId !== 'all'
      ? columnsData[sectionId]?.pages?.[pageId]?.actions
      : undefined;

  const activeSections = useMemo(
    () => values.filter((value) => value.includes('SECTION')),
    [values],
  );

  const activePages = useMemo(
    () =>
      values.filter(
        (value) => !value.includes('SECTION') && !value.includes('_ACTION'),
      ),
    [values],
  );

  const activeActions = useMemo(
    () => values.filter((value) => value.includes('_ACTION')),
    [values],
  );

  const allActionsSection =
    templateId && sectionId ? sectionActions[sectionId] : undefined;

  const onChange = (
    key: NotificationTemplateTypes | NotificationTemplateTypes[],
    value: boolean,
    columnsDataItem: any,
  ) => {
    if (Array.isArray(key)) {
      onChangeHelperArray(
        key,
        value,
        columnsDataItem,
        columnsData,
        setValues,
        activePages,
        pageId,
      );
    } else {
      onChangeHelperSingle(
        key,
        value,
        columnsDataItem,
        columnsData,
        setValues,
        activePages,
        activeActions,
      );
    }
  };

  const onSelectRow = (
    key: NotificationTemplateTypes | 'all',
    name: 'sectionId' | 'pageId',
  ) => {
    if (name === 'sectionId') {
      dispatch(onSectionChange(key as NotificationTemplateTypes));
    } else if (name === 'pageId') {
      dispatch(onPageChange(key as NotificationTemplateTypes));
    }
  };

  const onCancel = () => {
    dispatch(handleConfirmPopup(false));

    if (selectedTemplate) {
      const notificationsTypes = ([] as NotificationTemplateTypes[]).concat(
        selectedTemplate?.notificationsTypes,
      );

      if (columnsData) {
        Object.keys(columnsData).forEach((key) => {
          if (
            columnsData &&
            columnsData[key] &&
            (columnsData[key] as any).pages
          ) {
            Object.keys((columnsData[key] as any).pages).forEach((page) => {
              if (
                columnsData &&
                columnsData[key] &&
                (columnsData[key] as any).pages &&
                (columnsData[key] as any).pages[page]
              ) {
                const actionsValues: NotificationTemplateTypes[] = (
                  columnsData[key] as any
                ).pages[page]?.actionsKeys;

                actionsValues.forEach((action) => {
                  if (
                    notificationsTypes?.includes(action) &&
                    !notificationsTypes.includes(
                      page as NotificationTemplateTypes,
                    )
                  ) {
                    notificationsTypes.push(page as NotificationTemplateTypes);
                  }
                });

                actionsValues.forEach((action) => {
                  if (
                    notificationsTypes?.includes(action) &&
                    !notificationsTypes.includes(
                      key as NotificationTemplateTypes,
                    )
                  ) {
                    notificationsTypes.push(key as NotificationTemplateTypes);
                  }
                });
              }
            });
          }
        });
      }

      setValues(notificationsTypes);
    }
  };

  const onSubmit = async () => {
    const availableEnums = values
      ?.filter(
        (item) =>
          item !== ('SECTION_UNASSIGNED' as NotificationTemplateTypes) &&
          item !== ('UNASSIGNED_LIST' as NotificationTemplateTypes),
      )
      ?.filter((item) => item.includes('_ACTION'))
      .map((item) => item.replace('_ACTION', ''));

    await editTemplate({
      id: selectedTemplate?.id as string,
      body: {
        notificationsTypes: availableEnums as NotificationTemplateTypes[],
      },
    });
  };

  const onSearchSettings = (value: string) => {
    dispatch(onSearch(value));
  };

  useEffect(() => {
    if (!pageId && sections) {
      onSelectRow(
        sections[sectionId]?.pagesKeys[0] as NotificationTemplateTypes,
        'pageId',
      );

      dispatch(onSectionChange(sectionId));
    }
  }, [sectionId, sections]);

  useEffect(() => {
    if (selectedTemplate) {
      const notificationsTypes = ([] as NotificationTemplateTypes[]).concat(
        selectedTemplate?.notificationsTypes,
      );

      if (columnsData) {
        Object.keys(columnsData).forEach((key) => {
          if (
            columnsData &&
            columnsData[key] &&
            (columnsData[key] as any).pages
          ) {
            Object.keys((columnsData[key] as any).pages).forEach((page) => {
              if (
                columnsData &&
                columnsData[key] &&
                (columnsData[key] as any).pages &&
                (columnsData[key] as any).pages[page]
              ) {
                const actionsValues: NotificationTemplateTypes[] = (
                  columnsData[key] as any
                ).pages[page]?.actionsKeys;

                actionsValues.forEach((action) => {
                  if (
                    notificationsTypes?.includes(action) &&
                    !notificationsTypes.includes(
                      page as NotificationTemplateTypes,
                    )
                  ) {
                    notificationsTypes.push(page as NotificationTemplateTypes);
                  }
                });

                actionsValues.forEach((action) => {
                  if (
                    notificationsTypes?.includes(action) &&
                    !notificationsTypes.includes(
                      key as NotificationTemplateTypes,
                    )
                  ) {
                    notificationsTypes.push(key as NotificationTemplateTypes);
                  }
                });
              }
            });
          }
        });
      }

      setValues(notificationsTypes || []);
    }
  }, [selectedTemplate?.notificationsTypes]);

  useEffect(() => {
    if (selectedTemplate && selectedTemplate.notificationsTypes && values) {
      const availableEnums = values?.filter((item) => item.includes('_ACTION'));

      const isDirty =
        selectedTemplate.notificationsTypes?.filter((item) =>
          availableEnums.every((value) => item !== value),
        )?.length > 0 ||
        availableEnums?.filter((item) =>
          selectedTemplate.notificationsTypes?.every(
            (permission) => item !== permission,
          ),
        )?.length;

      if (isDirty) {
        dispatch(handleConfirmPopup(true));
      } else {
        dispatch(handleConfirmPopup(false));
      }
    }
  }, [selectedTemplate, values]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(handleConfirmPopup(false));
    }
  }, [isSuccess]);

  return (
    <ColumnWithConfirmHOC
      onSubmit={onSubmit}
      onCancel={onCancel}
      editIsLoading={requestInProgress}
      buttonText={`Save settings to ${selectedTemplate?.name}`}
    >
      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activeSections}
          columnData={sections}
          onChange={onChange}
          onSelectRow={(key: NotificationTemplateTypes | 'all') => {
            onSelectRow(key, 'sectionId');

            dispatch(onPageChange(undefined));
          }}
          selectedId={sectionId}
          title="Category"
          emptyTitle="—"
          isLoading={isLoading}
          hasAllToggleButton={false}
          isEditable
        />
      </div>
      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activePages}
          columnData={pages}
          isLoading={isLoading}
          onChange={onChange}
          onSelectRow={(key: NotificationTemplateTypes | 'all') =>
            onSelectRow(key, 'pageId')
          }
          selectedId={pageId}
          allActionsSectionButton={Boolean(allActionsSection)}
          title="Group"
          emptyTitle="—"
          isEditable
        />
      </div>
      <div className={cn.columnWrapper}>
        <ColumnWithToggle
          activePermissions={activeActions}
          columnData={pageId !== 'all' ? actions : allActionsSectionFiltered}
          isLoading={isLoading}
          onChange={onChange}
          selectedId={pageId}
          title="Type"
          emptyTitle="View only"
          onSearch={onSearchSettings}
          search={search}
          isEditable
        />
      </div>
    </ColumnWithConfirmHOC>
  );
};

export default SettingsColumn;
